/**
 * Separation Planner — deterministic multi-step separation model (study-chemistry-tools-prd.md
 * §8.2 `separation-planner`, §4.4 Year 7 weeks 7 and 9).
 *
 * Mixture mode follows one main container. Each step acts on the container, sets one collected
 * fraction aside or lets a liquid escape, and the rest stays in the container. The plan stops at
 * the first step that cannot work at that point. Water-treatment mode tracks which contaminant
 * groups remain in the water after each treatment stage.
 */

export type SeparationMode = 'mixture' | 'water-treatment';

export interface SeparationModeOption {
  mode: SeparationMode;
  label: string;
}

export const SEPARATION_MODE_OPTIONS: readonly SeparationModeOption[] = [
  { mode: 'mixture', label: 'Separate a mixture' },
  { mode: 'water-treatment', label: 'Treat drinking water' },
];

/**
 * CT-MODE: both modes start in Year 7, and the mixture mode is the default for every Year. Water
 * treatment is an applied topic that the learner opens by choice.
 */
export const SEPARATION_YEAR_MODES: readonly { mode: SeparationMode; fromYear: number }[] = [
  { mode: 'mixture', fromYear: 7 },
];

export interface SeparationDataProvenance {
  sources: string[];
  conditions: string;
  reviewStatus: 'pending-teaching-review';
}

export const SEPARATION_DATA_PROVENANCE: SeparationDataProvenance = {
  sources: [
    'CRC Handbook of Chemistry and Physics — normal boiling points of water (100 °C) and ethanol (78 °C) at 101.325 kPa; water solubility of sodium chloride and copper(II) sulfate; insolubility of silica sand, calcium carbonate and sulfur.',
    'CRC Handbook of Chemistry and Physics — ethanol–water azeotrope at about 95.6% ethanol by mass, boiling at 78.2 °C (101.325 kPa); copper(II) sulfate pentahydrate (CuSO₄·5H₂O) is blue, anhydrous CuSO₄ is white.',
    'Standard secondary chemistry textbook descriptions of magnetic separation, sieving, dissolving, filtration, decanting, separating funnels, evaporation and crystallisation, distillation and paper chromatography.',
    'NHMRC/NRMMC Australian Drinking Water Guidelines (2011, updated) — multiple-barrier treatment: coagulation and flocculation, sedimentation, filtration and disinfection.',
    'WHO Guidelines for Drinking-water Quality (4th ed.) — chlorination and UV disinfection; turbidity reduces disinfection; desalination by reverse osmosis and thermal distillation.',
    'Membrane manufacturer guidance summarised in water-treatment texts — polyamide reverse-osmosis membranes are damaged by free chlorine, so feed water is dechlorinated or disinfected after the membranes.',
  ],
  conditions:
    'Classroom conditions, about 25 °C and 100 kPa. Boiling points are normal boiling points at 101.325 kPa. Water-treatment stages are idealised: each stage removes its target group completely.',
  reviewStatus: 'pending-teaching-review',
};

/** Longest plan a learner can build, and the depth used to search for other valid orders. */
export const MAX_PLAN_STEPS = 6;
export const VALID_ORDER_SEARCH_DEPTH = 5;

// ---------------------------------------------------------------------------------------------
// Mixture mode
// ---------------------------------------------------------------------------------------------

export type MixtureStepId =
  | 'magnet'
  | 'sieve'
  | 'dissolve'
  | 'filter'
  | 'decant'
  | 'separating-funnel'
  | 'evaporate'
  | 'distil'
  | 'chromatography';

export type ComponentForm = 'solid' | 'suspended' | 'dissolved' | 'liquid';

/** large = held by a kitchen sieve; coarse = settles in a minute; fine = stays cloudy for hours. */
export type ParticleSize = 'large' | 'coarse' | 'fine';

export interface MixtureComponent {
  id: string;
  name: string;
  formula?: string;
  kind: 'solid' | 'liquid';
  magnetic: boolean;
  solubleInWater: boolean;
  size?: ParticleSize;
  /** Normal boiling point at 101.325 kPa, for liquids that can be distilled. */
  boilingPointC?: number;
  immiscibleWithWater?: boolean;
  dye?: boolean;
}

export interface VesselItem {
  componentId: string;
  form: ComponentForm;
}

export interface MixtureCase {
  id: string;
  title: string;
  context: string;
  goal: string;
  start: VesselItem[];
  /** Components that must end up collected on their own. */
  recover: string[];
  referencePlan: MixtureStepId[];
  teachingPoint: string;
}

export interface MixtureStepInfo {
  id: MixtureStepId;
  label: string;
  property: string;
}

export const MIXTURE_STEPS: readonly MixtureStepInfo[] = [
  { id: 'magnet', label: 'Magnet', property: 'Pulls out magnetic solids such as iron.' },
  { id: 'sieve', label: 'Sieve', property: 'Holds back pieces larger than the holes.' },
  { id: 'dissolve', label: 'Add water and stir', property: 'Dissolves soluble solids; insoluble solids stay as grains.' },
  { id: 'filter', label: 'Filter', property: 'Filter paper traps undissolved solid; liquid and dissolved substances pass through.' },
  { id: 'decant', label: 'Decant', property: 'Pour liquid off a heavy solid that has settled.' },
  { id: 'separating-funnel', label: 'Separating funnel', property: 'Separates two liquids that form layers.' },
  { id: 'evaporate', label: 'Evaporate / crystallise', property: 'Heat drives off the liquid and leaves the dissolved solid as crystals.' },
  { id: 'distil', label: 'Distil', property: 'Boils the liquid with the lowest boiling point, then condenses and collects it.' },
  { id: 'chromatography', label: 'Paper chromatography', property: 'Separates dissolved dyes that travel up paper at different speeds.' },
];

export const MIXTURE_COMPONENTS: Record<string, MixtureComponent> = {
  water: { id: 'water', name: 'water', formula: 'H₂O', kind: 'liquid', magnetic: false, solubleInWater: true, boilingPointC: 100 },
  ethanol: { id: 'ethanol', name: 'ethanol', formula: 'C₂H₅OH', kind: 'liquid', magnetic: false, solubleInWater: true, boilingPointC: 78 },
  oil: { id: 'oil', name: 'vegetable oil', kind: 'liquid', magnetic: false, solubleInWater: false, immiscibleWithWater: true },
  salt: { id: 'salt', name: 'salt', formula: 'NaCl', kind: 'solid', magnetic: false, solubleInWater: true, size: 'coarse' },
  sand: { id: 'sand', name: 'sand', formula: 'SiO₂', kind: 'solid', magnetic: false, solubleInWater: false, size: 'coarse' },
  iron: { id: 'iron', name: 'iron filings', formula: 'Fe', kind: 'solid', magnetic: true, solubleInWater: false, size: 'coarse' },
  gravel: { id: 'gravel', name: 'gravel', kind: 'solid', magnetic: false, solubleInWater: false, size: 'large' },
  clay: { id: 'clay', name: 'clay', kind: 'solid', magnetic: false, solubleInWater: false, size: 'fine' },
  chalk: { id: 'chalk', name: 'chalk powder', formula: 'CaCO₃', kind: 'solid', magnetic: false, solubleInWater: false, size: 'fine' },
  sulfur: { id: 'sulfur', name: 'sulfur powder', formula: 'S', kind: 'solid', magnetic: false, solubleInWater: false, size: 'coarse' },
  'copper-sulfate': { id: 'copper-sulfate', name: 'copper(II) sulfate', formula: 'CuSO₄·5H₂O', kind: 'solid', magnetic: false, solubleInWater: true, size: 'coarse' },
  'dye-blue': { id: 'dye-blue', name: 'blue dye', kind: 'solid', magnetic: false, solubleInWater: true, size: 'fine', dye: true },
  'dye-yellow': { id: 'dye-yellow', name: 'yellow dye', kind: 'solid', magnetic: false, solubleInWater: true, size: 'fine', dye: true },
  'dye-red': { id: 'dye-red', name: 'red dye', kind: 'solid', magnetic: false, solubleInWater: true, size: 'fine', dye: true },
};

export const MIXTURE_CASES: readonly MixtureCase[] = [
  {
    id: 'salt-sand-iron',
    title: 'Salt, sand and iron filings',
    context: 'A dry mixture from a spilled practical tray.',
    goal: 'Recover the salt, the sand and the iron filings separately.',
    start: [{ componentId: 'salt', form: 'solid' }, { componentId: 'sand', form: 'solid' }, { componentId: 'iron', form: 'solid' }],
    recover: ['salt', 'sand', 'iron'],
    referencePlan: ['magnet', 'dissolve', 'filter', 'evaporate'],
    teachingPoint: 'Use one property at a time: magnetism, then solubility, then particle size, then volatility.',
  },
  {
    id: 'beach-sample',
    title: 'Beach sample: gravel, sand and salt',
    context: 'A scoop of dry beach material with pebbles, sand and dried sea salt.',
    goal: 'Recover the gravel, the sand and the salt separately.',
    start: [{ componentId: 'gravel', form: 'solid' }, { componentId: 'sand', form: 'solid' }, { componentId: 'salt', form: 'solid' }],
    recover: ['gravel', 'sand', 'salt'],
    referencePlan: ['sieve', 'dissolve', 'filter', 'evaporate'],
    teachingPoint: 'A sieve separates by size only. Sand and salt grains are a similar size, so solubility must separate them.',
  },
  {
    id: 'seawater',
    title: 'Seawater',
    context: 'Clear seawater: salt dissolved in water.',
    goal: 'Collect both fresh water and salt.',
    start: [{ componentId: 'water', form: 'liquid' }, { componentId: 'salt', form: 'dissolved' }],
    recover: ['water', 'salt'],
    referencePlan: ['distil'],
    teachingPoint: 'Evaporation recovers the salt but loses the water. Distillation condenses the vapour so both are kept.',
  },
  {
    id: 'black-ink',
    title: 'Black ink',
    context: 'A water-based black ink made from three dissolved dyes.',
    goal: 'Separate the three dyes from each other.',
    start: [{ componentId: 'water', form: 'liquid' }, { componentId: 'dye-blue', form: 'dissolved' }, { componentId: 'dye-yellow', form: 'dissolved' }, { componentId: 'dye-red', form: 'dissolved' }],
    recover: ['dye-blue', 'dye-yellow', 'dye-red'],
    referencePlan: ['chromatography'],
    teachingPoint: 'All the dyes are dissolved, so filtering and evaporating cannot split them. They differ in how far they travel up paper.',
  },
  {
    id: 'oil-salt-water',
    title: 'Oil, salt and water',
    context: 'Salty water with a layer of vegetable oil floating on top.',
    goal: 'Recover the oil and the salt separately.',
    start: [{ componentId: 'oil', form: 'liquid' }, { componentId: 'water', form: 'liquid' }, { componentId: 'salt', form: 'dissolved' }],
    recover: ['oil', 'salt'],
    referencePlan: ['separating-funnel', 'evaporate'],
    teachingPoint: 'Separate the liquids that form layers first. Then the salty water is an ordinary solution.',
  },
  {
    id: 'muddy-water',
    title: 'Muddy water',
    context: 'Water clouded by very fine clay that stays suspended for hours.',
    goal: 'Recover clear water.',
    start: [{ componentId: 'water', form: 'liquid' }, { componentId: 'clay', form: 'suspended' }],
    recover: ['water'],
    referencePlan: ['filter'],
    teachingPoint: 'Fine particles do not settle quickly, so decanting fails. Filter paper holds them back.',
  },
  {
    id: 'sand-water',
    title: 'Sand in water',
    context: 'Coarse sand that sinks to the bottom of a beaker of water within a minute.',
    goal: 'Recover the sand and the water separately.',
    start: [{ componentId: 'water', form: 'liquid' }, { componentId: 'sand', form: 'suspended' }],
    recover: ['sand', 'water'],
    referencePlan: ['decant'],
    teachingPoint: 'Heavy grains settle, so decanting is quick. Filtering also works and leaves less water with the sand.',
  },
  {
    id: 'ethanol-water',
    title: 'Ethanol and water',
    context: 'Two liquids that mix completely, so no layers form.',
    goal: 'Collect an ethanol-rich distillate and leave the water behind.',
    start: [{ componentId: 'water', form: 'liquid' }, { componentId: 'ethanol', form: 'liquid' }],
    recover: ['ethanol', 'water'],
    referencePlan: ['distil'],
    teachingPoint: 'Liquids that mix cannot be poured or filtered apart. Liquids with close boiling points need fractional distillation (a fractionating column). Even then the distillate is at most about 95% ethanol, so the separation is never complete.',
  },
  {
    id: 'iron-sulfur',
    title: 'Iron filings and sulfur powder',
    context: 'A yellow-grey powder mixture before any heating.',
    goal: 'Recover the iron and the sulfur separately.',
    start: [{ componentId: 'iron', form: 'solid' }, { componentId: 'sulfur', form: 'solid' }],
    recover: ['iron', 'sulfur'],
    referencePlan: ['magnet'],
    teachingPoint: 'In a mixture, iron keeps its magnetism. After heating they react to form iron(II) sulfide, which a magnet cannot separate.',
  },
  {
    id: 'copper-sulfate-chalk',
    title: 'Copper(II) sulfate with chalk',
    context: 'Blue copper(II) sulfate powder contaminated with white chalk dust.',
    goal: 'Grow clean copper(II) sulfate crystals.',
    start: [{ componentId: 'copper-sulfate', form: 'solid' }, { componentId: 'chalk', form: 'solid' }],
    recover: ['copper-sulfate'],
    referencePlan: ['dissolve', 'filter', 'evaporate'],
    teachingPoint: 'Chalk does not dissolve and is too fine to settle quickly, so filtration removes it before crystallising.',
  },
];

export type StepStatus = 'worked' | 'failed';

export interface CollectedFraction {
  componentIds: string[];
  how: 'collected' | 'lost';
  label: string;
}

export interface MixtureStepOutcome {
  stepId: MixtureStepId;
  status: StepStatus;
  message: string;
  fractions: CollectedFraction[];
  after: VesselItem[];
}

export interface MixturePlanResult {
  status: 'success' | 'failed' | 'unsupported';
  outcomes: MixtureStepOutcome[];
  /** Index of the failed step, or -1 when every step worked. */
  failedAt: number;
  fractions: CollectedFraction[];
  finalVessel: VesselItem[];
  summary: string;
}

const MIXTURE_STEP_IDS = new Set<string>(MIXTURE_STEPS.map((step) => step.id));

export function isMixtureStepId(value: string): value is MixtureStepId {
  return MIXTURE_STEP_IDS.has(value);
}

export function mixtureStepLabel(id: MixtureStepId): string {
  return MIXTURE_STEPS.find((step) => step.id === id)?.label ?? id;
}

export function getMixtureCase(id: string): MixtureCase | null {
  return MIXTURE_CASES.find((item) => item.id === id) ?? null;
}

function component(id: string): MixtureComponent {
  const found = MIXTURE_COMPONENTS[id];
  if (!found) throw new Error(`Unknown mixture component: ${id}`);
  return found;
}

export function componentName(id: string): string {
  return component(id).name;
}

/** Joins names as "a", "a and b" or "a, b and c". */
export function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function namesOf(items: VesselItem[]): string {
  return joinNames(items.map((item) => componentName(item.componentId)));
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const isUndissolvedSolid = (item: VesselItem) => item.form === 'solid' || item.form === 'suspended';

function failed(stepId: MixtureStepId, vessel: VesselItem[], message: string): MixtureStepOutcome {
  return { stepId, status: 'failed', message, fractions: [], after: vessel };
}

function worked(
  stepId: MixtureStepId,
  after: VesselItem[],
  message: string,
  fractions: CollectedFraction[] = [],
): MixtureStepOutcome {
  return { stepId, status: 'worked', message, fractions, after };
}

function without(vessel: VesselItem[], removed: VesselItem[]): VesselItem[] {
  const ids = new Set(removed.map((item) => item.componentId));
  return vessel.filter((item) => !ids.has(item.componentId));
}

function collect(items: VesselItem[], label: string): CollectedFraction {
  return { componentIds: items.map((item) => item.componentId), how: 'collected', label };
}

function impureResidueMessage(stepLabel: string, solids: VesselItem[]): string {
  return `${stepLabel} would trap ${namesOf(solids)} together in one residue. It cannot split two undissolved solids — separate them by another property (such as magnetism or size) before this step.`;
}

function runMagnet(vessel: VesselItem[]): MixtureStepOutcome {
  const magnetic = vessel.filter((item) => isUndissolvedSolid(item) && component(item.componentId).magnetic);
  if (magnetic.length === 0) {
    return failed('magnet', vessel, `Nothing magnetic is left: ${namesOf(vessel)} are not attracted to a magnet, so the magnet picks up nothing.`);
  }
  const rest = without(vessel, magnetic);
  return worked(
    'magnet',
    rest,
    `The magnet pulls out the ${namesOf(magnetic)} because iron is magnetic. The ${namesOf(rest)} stay behind.`,
    [collect(magnetic, 'Held by the magnet')],
  );
}

function runSieve(vessel: VesselItem[]): MixtureStepOutcome {
  const large = vessel.filter((item) => isUndissolvedSolid(item) && component(item.componentId).size === 'large');
  if (large.length === 0) {
    return failed('sieve', vessel, `No pieces are bigger than the sieve holes. The ${namesOf(vessel)} would all pass through together.`);
  }
  const rest = without(vessel, large);
  return worked(
    'sieve',
    rest,
    `The sieve holds back the ${namesOf(large)} because the pieces are larger than the holes. The smaller ${namesOf(rest)} pass through.`,
    [collect(large, 'Held in the sieve')],
  );
}

function runDissolve(vessel: VesselItem[]): MixtureStepOutcome {
  const dry = vessel.filter((item) => item.form === 'solid');
  if (dry.length === 0) {
    return failed('dissolve', vessel, 'There is no dry solid left. Adding more water now only dilutes the mixture and separates nothing.');
  }
  const soluble = dry.filter((item) => component(item.componentId).solubleInWater);
  if (soluble.length === 0) {
    return failed('dissolve', vessel, `${capitalise(namesOf(dry))} do not dissolve in water, so stirring in water only makes the mixture wet.`);
  }
  const insoluble = dry.filter((item) => !component(item.componentId).solubleInWater);
  const after: VesselItem[] = vessel.map((item) => {
    if (item.form !== 'solid') return item;
    return { componentId: item.componentId, form: component(item.componentId).solubleInWater ? 'dissolved' : 'suspended' };
  });
  if (!after.some((item) => item.componentId === 'water')) after.push({ componentId: 'water', form: 'liquid' });
  const insolubleNote = insoluble.length > 0
    ? ` The ${namesOf(insoluble)} do not dissolve and stay as grains in the liquid.`
    : '';
  return worked('dissolve', after, `Water dissolves the ${namesOf(soluble)}: its particles spread through the water.${insolubleNote}`);
}

function runFilter(vessel: VesselItem[]): MixtureStepOutcome {
  if (!vessel.some((item) => item.form === 'liquid')) {
    return failed('filter', vessel, 'Filtering needs a liquid to carry substances through the paper. The mixture is still dry — add water first.');
  }
  const suspended = vessel.filter((item) => item.form === 'suspended');
  if (suspended.length === 0) {
    return failed('filter', vessel, `Nothing undissolved is in the liquid, so the ${namesOf(vessel)} all pass through the filter paper together.`);
  }
  if (suspended.length > 1) return failed('filter', vessel, impureResidueMessage('Filtering', suspended));
  const rest = without(vessel, suspended);
  return worked(
    'filter',
    rest,
    `The filter paper traps the ${namesOf(suspended)} as the residue. The ${namesOf(rest)} pass through as the filtrate.`,
    [collect(suspended, 'Residue on the filter paper')],
  );
}

function runDecant(vessel: VesselItem[]): MixtureStepOutcome {
  if (!vessel.some((item) => item.form === 'liquid')) {
    return failed('decant', vessel, 'Decanting means pouring a liquid off a settled solid. There is no liquid to pour yet.');
  }
  const suspended = vessel.filter((item) => item.form === 'suspended');
  if (suspended.length === 0) {
    return failed('decant', vessel, 'No solid has settled at the bottom, so pouring the liquid off separates nothing.');
  }
  const fine = suspended.filter((item) => component(item.componentId).size === 'fine');
  if (fine.length > 0) {
    return failed('decant', vessel, `The ${namesOf(fine)} particles are too fine to settle quickly, so they pour over with the liquid. Filter instead.`);
  }
  if (suspended.length > 1) return failed('decant', vessel, impureResidueMessage('Decanting', suspended));
  const rest = without(vessel, suspended);
  return worked(
    'decant',
    rest,
    `The ${namesOf(suspended)} settle to the bottom, so the ${namesOf(rest)} can be poured off. A little liquid stays with the solid, so decanting is quick but less complete than filtering.`,
    [collect(suspended, 'Settled solid left after pouring')],
  );
}

function runSeparatingFunnel(vessel: VesselItem[]): MixtureStepOutcome {
  const liquids = vessel.filter((item) => item.form === 'liquid');
  const layer = liquids.filter((item) => component(item.componentId).immiscibleWithWater);
  if (liquids.length < 2 || layer.length === 0) {
    return failed('separating-funnel', vessel, 'A separating funnel needs two liquids that form layers. Here nothing forms a separate layer.');
  }
  const suspended = vessel.filter((item) => item.form === 'suspended');
  if (suspended.length > 0) {
    return failed('separating-funnel', vessel, `Remove the undissolved ${namesOf(suspended)} first. It would block the tap and come out with the bottom layer.`);
  }
  const rest = without(vessel, layer);
  return worked(
    'separating-funnel',
    rest,
    `The ${namesOf(layer)} does not mix with water and is less dense, so it floats as the top layer. Drain the ${namesOf(rest)} from the tap, then collect the ${namesOf(layer)}.`,
    [collect(layer, 'Top layer from the funnel')],
  );
}

function runEvaporate(vessel: VesselItem[]): MixtureStepOutcome {
  const liquids = vessel.filter((item) => item.form === 'liquid');
  if (liquids.length === 0) {
    return failed('evaporate', vessel, 'There is no liquid to evaporate. Evaporation recovers a solid that is dissolved in a liquid.');
  }
  const layer = liquids.filter((item) => component(item.componentId).immiscibleWithWater);
  if (layer.length > 0) {
    return failed('evaporate', vessel, `The ${namesOf(layer)} does not evaporate on a hotplate and would coat the crystals. Use a separating funnel first.`);
  }
  const dissolved = vessel.filter((item) => item.form === 'dissolved');
  if (dissolved.length === 0) {
    return failed('evaporate', vessel, `Nothing is dissolved, so heating only sends the ${namesOf(liquids)} into the air and nothing is collected.`);
  }
  const suspended = vessel.filter((item) => item.form === 'suspended');
  if (suspended.length > 0) {
    return failed('evaporate', vessel, `Evaporating now would leave the ${namesOf(dissolved)} crystals mixed through the ${namesOf(suspended)}. Remove the undissolved solid first.`);
  }
  if (dissolved.length > 1) {
    return failed('evaporate', vessel, `The ${namesOf(dissolved)} would all be left together as one mixed solid. Evaporation cannot split substances that are all dissolved.`);
  }
  const after: VesselItem[] = dissolved.map((item) => ({ componentId: item.componentId, form: 'solid' }));
  return worked(
    'evaporate',
    after,
    `Heating drives off the ${namesOf(liquids)} as vapour, which escapes into the air. The ${namesOf(dissolved)} is left behind as crystals.`,
    [{ componentIds: liquids.map((item) => item.componentId), how: 'lost', label: 'Escaped as vapour' }],
  );
}

function boilingPoint(item: VesselItem): number {
  const value = component(item.componentId).boilingPointC;
  if (value === undefined) throw new Error(`No boiling point for ${item.componentId}`);
  return value;
}

/** Two mixed liquids never separate completely; ethanol–water stops at the azeotrope (about 95.6% ethanol by mass, 78.2 °C). */
const MISCIBLE_LIQUIDS_NOTE =
  'Both liquids boil off together to some extent, so mixed liquids with close boiling points need fractional distillation (a fractionating column). The distillate is enriched, not pure: for ethanol and water it tops out near 95% ethanol.';

function runDistil(vessel: VesselItem[]): MixtureStepOutcome {
  const liquids = vessel.filter((item) => item.form === 'liquid');
  if (liquids.length === 0) {
    return failed('distil', vessel, 'There is no liquid to boil. Distillation collects a liquid by boiling and condensing it.');
  }
  const layer = liquids.filter((item) => component(item.componentId).immiscibleWithWater);
  if (layer.length > 0) {
    return failed('distil', vessel, `Liquids that form layers, such as ${namesOf(layer)}, are separated with a separating funnel, not by heating.`);
  }
  const suspended = vessel.filter((item) => item.form === 'suspended');
  if (suspended.length > 0) {
    return failed('distil', vessel, `Filter out the undissolved ${namesOf(suspended)} before distilling. Solids in the flask cause uneven boiling and stay mixed with what is left.`);
  }
  const dissolved = vessel.filter((item) => item.form === 'dissolved');
  if (liquids.length === 1 && dissolved.length === 0) {
    return failed('distil', vessel, `The ${namesOf(liquids)} is already on its own. Distilling it changes nothing.`);
  }
  const sorted = [...liquids].sort((a, b) => boilingPoint(a) - boilingPoint(b));
  const distillate = sorted[0];
  const remaining = without(vessel, [distillate]).map<VesselItem>((item) =>
    item.form === 'dissolved' && liquids.length === 1 ? { componentId: item.componentId, form: 'solid' } : item,
  );
  const distillateName = componentName(distillate.componentId);
  const reason = liquids.length > 1
    ? `It has the lowest boiling point (${boilingPoint(distillate)} °C, compared with ${joinNames(sorted.slice(1).map((item) => `${boilingPoint(item)} °C`))}), so it boils off first.`
    : `It boils at ${boilingPoint(distillate)} °C; dissolved solids do not boil off.`;
  return worked(
    'distil',
    remaining,
    `The ${distillateName} boils, its vapour condenses in the cooled condenser and drips into a clean flask. ${reason}${liquids.length > 1 ? ` ${MISCIBLE_LIQUIDS_NOTE}` : ''} Left in the flask: ${namesOf(remaining)}.`,
    [collect([distillate], liquids.length > 1 ? `${capitalise(distillateName)}-rich distillate from the fractionating column` : 'Distillate from the condenser')],
  );
}

function runChromatography(vessel: VesselItem[]): MixtureStepOutcome {
  const dyes = vessel.filter((item) => item.form === 'dissolved' && component(item.componentId).dye);
  if (dyes.length < 2) {
    return failed('chromatography', vessel, 'Paper chromatography separates a mixture of dissolved coloured substances. There is no dissolved dye mixture here.');
  }
  const rest = without(vessel, dyes);
  return worked(
    'chromatography',
    rest,
    `The solvent carries each dye up the paper at a different speed, depending on how strongly it is attracted to the paper. The ${namesOf(dyes)} form separate spots.`,
    dyes.map((dye) => collect([dye], `Spot of ${componentName(dye.componentId)}`)),
  );
}

const MIXTURE_RUNNERS: Record<MixtureStepId, (vessel: VesselItem[]) => MixtureStepOutcome> = {
  magnet: runMagnet,
  sieve: runSieve,
  dissolve: runDissolve,
  filter: runFilter,
  decant: runDecant,
  'separating-funnel': runSeparatingFunnel,
  evaporate: runEvaporate,
  distil: runDistil,
  chromatography: runChromatography,
};

/** Runs one step on the current container. Pure: the input array is never changed. */
export function runMixtureStep(stepId: MixtureStepId, vessel: VesselItem[]): MixtureStepOutcome {
  if (vessel.length <= 1) {
    return failed(stepId, vessel, `The container already holds only ${namesOf(vessel) || 'nothing'}. There is nothing left to separate.`);
  }
  return MIXTURE_RUNNERS[stepId](vessel);
}

function missingRecoveries(mixture: MixtureCase, fractions: CollectedFraction[], vessel: VesselItem[]): string[] {
  const kept = new Set<string>();
  fractions
    .filter((fraction) => fraction.how === 'collected' && fraction.componentIds.length === 1)
    .forEach((fraction) => kept.add(fraction.componentIds[0]));
  if (vessel.length === 1) kept.add(vessel[0].componentId);
  return mixture.recover.filter((id) => !kept.has(id));
}

/** Executes a whole plan. Fails closed on an unknown case or step, an empty plan or a long plan. */
export function runMixturePlan(caseId: string, plan: readonly string[]): MixturePlanResult {
  const mixture = getMixtureCase(caseId);
  const unsupported = (summary: string): MixturePlanResult => ({
    status: 'unsupported',
    outcomes: [],
    failedAt: -1,
    fractions: [],
    finalVessel: mixture?.start ?? [],
    summary,
  });
  if (!mixture) return unsupported('This mixture is not in the reviewed case list.');
  if (plan.length === 0) return unsupported('Add at least one step to the plan.');
  if (plan.length > MAX_PLAN_STEPS) return unsupported(`Plans are limited to ${MAX_PLAN_STEPS} steps.`);
  if (!plan.every(isMixtureStepId)) return unsupported('The plan contains a step that this model does not support.');

  let vessel = mixture.start.map((item) => ({ ...item }));
  const outcomes: MixtureStepOutcome[] = [];
  const fractions: CollectedFraction[] = [];
  for (let index = 0; index < plan.length; index += 1) {
    const outcome = runMixtureStep(plan[index] as MixtureStepId, vessel);
    outcomes.push(outcome);
    if (outcome.status === 'failed') {
      return {
        status: 'failed',
        outcomes,
        failedAt: index,
        fractions,
        finalVessel: vessel,
        summary: `Step ${index + 1} (${mixtureStepLabel(outcome.stepId)}) cannot work at this point. ${outcome.message}`,
      };
    }
    fractions.push(...outcome.fractions);
    vessel = outcome.after;
  }

  if (vessel.length > 1) {
    return {
      status: 'failed',
      outcomes,
      failedAt: -1,
      fractions,
      finalVessel: vessel,
      summary: `Not finished: the container still holds ${namesOf(vessel)} mixed together. Add a step that uses a property they do not share.`,
    };
  }
  const missing = missingRecoveries(mixture, fractions, vessel);
  if (missing.length > 0) {
    const lost = fractions.some((fraction) => fraction.how === 'lost' && missing.some((id) => fraction.componentIds.includes(id)));
    return {
      status: 'failed',
      outcomes,
      failedAt: -1,
      fractions,
      finalVessel: vessel,
      summary: lost
        ? `The ${joinNames(missing.map(componentName))} escaped as vapour, but the goal asks you to keep it. Distillation condenses the vapour so it can be collected.`
        : `The ${joinNames(missing.map(componentName))} did not end up collected on its own.`,
    };
  }
  return {
    status: 'success',
    outcomes,
    failedAt: -1,
    fractions,
    finalVessel: vessel,
    summary: `Every step worked at its point in the plan and the goal is met: ${mixture.goal.charAt(0).toLowerCase()}${mixture.goal.slice(1)}`,
  };
}

/** Every order of distinct steps (up to the search depth) that reaches the goal. */
export function validMixturePlans(caseId: string, maxSteps = VALID_ORDER_SEARCH_DEPTH): MixtureStepId[][] {
  const mixture = getMixtureCase(caseId);
  if (!mixture) return [];
  const found: MixtureStepId[][] = [];
  const search = (plan: MixtureStepId[], vessel: VesselItem[]) => {
    if (plan.length > 0 && runMixturePlan(caseId, plan).status === 'success') {
      found.push(plan);
      return;
    }
    if (plan.length >= maxSteps) return;
    for (const step of MIXTURE_STEPS) {
      if (plan.includes(step.id)) continue;
      const outcome = runMixtureStep(step.id, vessel);
      if (outcome.status === 'worked') search([...plan, step.id], outcome.after);
    }
  };
  search([], mixture.start);
  return found.sort((a, b) => a.length - b.length);
}

// ---------------------------------------------------------------------------------------------
// Water-treatment mode
// ---------------------------------------------------------------------------------------------

export type WaterStepId =
  | 'screen'
  | 'coagulate'
  | 'sediment'
  | 'filter'
  | 'chlorinate'
  | 'uv'
  | 'reverse-osmosis'
  | 'distil';

export type Contaminant =
  | 'debris'
  | 'grit'
  | 'fine-clay'
  | 'floc'
  | 'residual-particles'
  | 'microbes'
  | 'salt';

export interface WaterStepInfo {
  id: WaterStepId;
  label: string;
  property: string;
}

export const WATER_STEPS: readonly WaterStepInfo[] = [
  { id: 'screen', label: 'Screening', property: 'Bars and mesh hold back leaves, sticks and litter.' },
  { id: 'coagulate', label: 'Coagulation & flocculation', property: 'A coagulant such as alum clumps tiny particles into floc.' },
  { id: 'sediment', label: 'Sedimentation', property: 'Slow-moving water lets heavy grit and floc sink.' },
  { id: 'filter', label: 'Sand filtration', property: 'Beds of sand trap small particles left after settling.' },
  { id: 'chlorinate', label: 'Disinfection: chlorine', property: 'Kills microbes and leaves a protective residual in the pipes.' },
  { id: 'uv', label: 'Disinfection: UV light', property: 'Ultraviolet light inactivates microbes; leaves no residual.' },
  { id: 'reverse-osmosis', label: 'Desalination: reverse osmosis', property: 'Pressure pushes water through a membrane that holds back dissolved salts.' },
  { id: 'distil', label: 'Desalination: distillation', property: 'Boiling and condensing leaves dissolved salts behind; uses a lot of energy.' },
];

export const CONTAMINANT_LABELS: Record<Contaminant, string> = {
  debris: 'Large debris (leaves, sticks, litter)',
  grit: 'Grit and sand',
  'fine-clay': 'Fine clay and silt (cloudiness)',
  floc: 'Floc (clumped particles)',
  'residual-particles': 'Small leftover particles',
  microbes: 'Disease-causing microbes',
  salt: 'Dissolved salts',
};

export const CONTAMINANT_ORDER: readonly Contaminant[] = [
  'debris', 'grit', 'fine-clay', 'floc', 'residual-particles', 'salt', 'microbes',
];

const PARTICLE_CONTAMINANTS: readonly Contaminant[] = ['debris', 'grit', 'fine-clay', 'floc', 'residual-particles'];
const DISINFECTION_STEPS: readonly WaterStepId[] = ['chlorinate', 'uv'];

export interface WaterScenario {
  id: string;
  title: string;
  context: string;
  contaminants: Contaminant[];
  referencePlan: WaterStepId[];
  teachingPoint: string;
}

export const WATER_SCENARIOS: readonly WaterScenario[] = [
  {
    id: 'river',
    title: 'River water',
    context: 'A town supply drawn from a slow river: leaves and sticks, cloudy fine clay and microbes.',
    contaminants: ['debris', 'fine-clay', 'microbes'],
    referencePlan: ['screen', 'coagulate', 'sediment', 'filter', 'chlorinate'],
    teachingPoint: 'Clear the water before disinfecting it: particles shield microbes from chlorine and UV.',
  },
  {
    id: 'flood-river',
    title: 'River after a flood',
    context: 'Fast, brown flood water carrying branches, heavy grit, fine clay and microbes.',
    contaminants: ['debris', 'grit', 'fine-clay', 'microbes'],
    referencePlan: ['screen', 'coagulate', 'sediment', 'filter', 'chlorinate'],
    teachingPoint: 'Sedimentation removes grit and floc together once coagulation has clumped the clay.',
  },
  {
    id: 'seawater',
    title: 'Seawater for a desalination plant',
    context: 'Ocean intake water with seaweed, fine silt, dissolved salts and microbes.',
    contaminants: ['debris', 'fine-clay', 'salt', 'microbes'],
    referencePlan: ['screen', 'coagulate', 'sediment', 'filter', 'reverse-osmosis', 'chlorinate'],
    teachingPoint: 'Pretreatment protects the membranes. Disinfect after desalination, because chlorine damages reverse-osmosis membranes.',
  },
  {
    id: 'stormwater',
    title: 'Urban stormwater harvesting',
    context: 'Runoff from streets collected for a park irrigation and toilet-flushing scheme: litter, grit, fine silt and microbes.',
    contaminants: ['debris', 'grit', 'fine-clay', 'microbes'],
    referencePlan: ['screen', 'coagulate', 'sediment', 'filter', 'uv'],
    teachingPoint: 'Stormwater carries litter and road grit, so screening and settling come first.',
  },
  {
    id: 'rainwater-tank',
    title: 'Rainwater tank',
    context: 'Roof runoff with leaves, settled roof dust and microbes from birds and animals.',
    contaminants: ['debris', 'grit', 'microbes'],
    referencePlan: ['screen', 'sediment', 'filter', 'uv'],
    teachingPoint: 'No fine clay is present, so coagulation is not needed. Settling and filtering still come before disinfection.',
  },
  {
    id: 'fresh-bore',
    title: 'Fresh groundwater bore',
    context: 'Water pumped from a sandy aquifer: fine sand from the bore and possible microbes. Not salty.',
    contaminants: ['grit', 'microbes'],
    referencePlan: ['sediment', 'filter', 'chlorinate'],
    teachingPoint: 'Fresh bore water does not need desalination. Treat only what is present.',
  },
  {
    id: 'brackish-bore',
    title: 'Brackish inland bore',
    context: 'Salty groundwater for a remote community: fine sand, dissolved salts and possible microbes.',
    contaminants: ['grit', 'salt', 'microbes'],
    referencePlan: ['sediment', 'filter', 'reverse-osmosis', 'chlorinate'],
    teachingPoint: 'Brackish water needs desalination, but particles must be removed first to protect the membrane.',
  },
  {
    id: 'reservoir',
    title: 'Dam reservoir water',
    context: 'Water drawn through a screened deep intake in a large dam: fine suspended clay and microbes, no litter.',
    contaminants: ['fine-clay', 'microbes'],
    referencePlan: ['coagulate', 'sediment', 'filter', 'chlorinate'],
    teachingPoint: 'The intake already keeps litter out, so start with coagulation to clump the fine clay before settling.',
  },
];

export interface WaterState {
  contaminants: Contaminant[];
  chlorineResidual: boolean;
}

export interface WaterStepOutcome {
  stepId: WaterStepId;
  status: StepStatus;
  message: string;
  removed: Contaminant[];
  after: WaterState;
}

export interface WaterPlanResult {
  status: 'success' | 'failed' | 'unsupported';
  outcomes: WaterStepOutcome[];
  failedAt: number;
  finalState: WaterState;
  summary: string;
}

const WATER_STEP_IDS = new Set<string>(WATER_STEPS.map((step) => step.id));

export function isWaterStepId(value: string): value is WaterStepId {
  return WATER_STEP_IDS.has(value);
}

export function waterStepLabel(id: WaterStepId): string {
  return WATER_STEPS.find((step) => step.id === id)?.label ?? id;
}

export function getWaterScenario(id: string): WaterScenario | null {
  return WATER_SCENARIOS.find((item) => item.id === id) ?? null;
}

function contaminantList(items: Contaminant[]): string {
  return joinNames(items.map((item) => CONTAMINANT_LABELS[item].toLowerCase()));
}

function waterFailed(stepId: WaterStepId, state: WaterState, message: string): WaterStepOutcome {
  return { stepId, status: 'failed', message, removed: [], after: state };
}

function applyRemoval(
  stepId: WaterStepId,
  state: WaterState,
  removed: Contaminant[],
  message: string,
  extra: { add?: Contaminant[]; chlorineResidual?: boolean } = {},
): WaterStepOutcome {
  const remaining = state.contaminants.filter((item) => !removed.includes(item));
  const added = (extra.add ?? []).filter((item) => !remaining.includes(item));
  return {
    stepId,
    status: 'worked',
    message,
    removed,
    after: {
      contaminants: [...remaining, ...added],
      chlorineResidual: extra.chlorineResidual ?? state.chlorineResidual,
    },
  };
}

/** Runs one treatment stage. Pure: the input state is never changed. */
export function runWaterStep(stepId: WaterStepId, state: WaterState): WaterStepOutcome {
  const has = (item: Contaminant) => state.contaminants.includes(item);
  if (stepId !== 'screen' && has('debris')) {
    return waterFailed(stepId, state, 'Screen first. Leaves, sticks and litter would block pumps, pipes and filters.');
  }
  const particles = PARTICLE_CONTAMINANTS.filter(has);
  switch (stepId) {
    case 'screen':
      if (!has('debris')) return waterFailed(stepId, state, 'There is no large debris, so screening removes nothing here.');
      return applyRemoval(stepId, state, ['debris'], 'Bars and mesh catch leaves, sticks and litter at the intake.');
    case 'coagulate':
      if (!has('fine-clay')) {
        return waterFailed(stepId, state, 'There are no fine suspended particles to clump, so adding a coagulant wastes chemicals.');
      }
      return applyRemoval(
        stepId,
        state,
        ['fine-clay'],
        'A coagulant such as aluminium sulfate (alum) cancels the charges that keep tiny clay particles apart. Gentle stirring (flocculation) clumps them into heavier floc.',
        { add: ['floc'] },
      );
    case 'sediment': {
      if (has('fine-clay')) {
        return waterFailed(stepId, state, 'Fine clay particles are so small, and repel each other so strongly, that they stay suspended for days. Coagulate first so they clump and sink.');
      }
      const settles = (['grit', 'floc'] as Contaminant[]).filter(has);
      if (settles.length === 0) return waterFailed(stepId, state, 'Nothing heavy is left to settle.');
      return applyRemoval(
        stepId,
        state,
        settles,
        `Slow-moving water in a settling tank lets the ${contaminantList(settles)} sink. A few small particles stay suspended.`,
        { add: ['residual-particles'] },
      );
    }
    case 'filter': {
      if (has('fine-clay')) {
        return waterFailed(stepId, state, 'Untreated fine clay passes through a sand filter or clogs it. Coagulate and settle it first.');
      }
      const heavy = (['grit', 'floc'] as Contaminant[]).filter(has);
      if (heavy.length > 0) {
        return waterFailed(stepId, state, `Settle the ${contaminantList(heavy)} first. Otherwise the sand filter clogs within hours.`);
      }
      if (!has('residual-particles')) return waterFailed(stepId, state, 'There are no particles left for the filter to trap.');
      return applyRemoval(stepId, state, ['residual-particles'], 'Beds of sand trap the small particles left after settling. The water is now clear — but clear water can still carry microbes.');
    }
    case 'chlorinate':
    case 'uv': {
      if (particles.length > 0) {
        return waterFailed(stepId, state, `Clear the water first: ${contaminantList(particles)} shield microbes from the disinfectant and use it up.`);
      }
      if (!has('microbes')) return waterFailed(stepId, state, 'Microbes have already been dealt with, so a second disinfection stage adds nothing.');
      return stepId === 'chlorinate'
        ? applyRemoval(stepId, state, ['microbes'], 'Chlorine kills bacteria and viruses. A small residual stays in the water and keeps protecting it in the pipes.', { chlorineResidual: true })
        : applyRemoval(stepId, state, ['microbes'], 'UV light damages the genetic material of microbes so they cannot multiply. It adds no chemical, so it leaves no residual in the pipes.');
    }
    case 'reverse-osmosis':
    case 'distil': {
      if (!has('salt')) {
        return waterFailed(stepId, state, 'This water is already fresh. Desalination uses a lot of energy and removes nothing that needs removing.');
      }
      if (particles.length > 0) {
        return waterFailed(stepId, state, `Pretreat first: ${contaminantList(particles)} would ${stepId === 'reverse-osmosis' ? 'foul the membranes' : 'foul the heat exchangers'}.`);
      }
      if (stepId === 'reverse-osmosis' && state.chlorineResidual) {
        return waterFailed(stepId, state, 'Chlorine in the feed water damages reverse-osmosis membranes. Disinfect after desalination.');
      }
      return stepId === 'reverse-osmosis'
        ? applyRemoval(stepId, state, ['salt'], 'High pressure pushes water molecules through a membrane; dissolved ions are held back in a salty brine.')
        : applyRemoval(stepId, state, ['salt'], 'Boiling and condensing produces fresh water; the dissolved salts stay behind in the brine. This uses much more energy than reverse osmosis.');
    }
    default:
      return waterFailed(stepId, state, 'This treatment stage is not supported by the model.');
  }
}

export function runWaterPlan(scenarioId: string, plan: readonly string[]): WaterPlanResult {
  const scenario = getWaterScenario(scenarioId);
  const start: WaterState = { contaminants: scenario ? [...scenario.contaminants] : [], chlorineResidual: false };
  const unsupported = (summary: string): WaterPlanResult => ({ status: 'unsupported', outcomes: [], failedAt: -1, finalState: start, summary });
  if (!scenario) return unsupported('This water source is not in the reviewed scenario list.');
  if (plan.length === 0) return unsupported('Add at least one treatment stage.');
  if (plan.length > MAX_PLAN_STEPS + 1) return unsupported(`Plans are limited to ${MAX_PLAN_STEPS + 1} stages.`);
  if (!plan.every(isWaterStepId)) return unsupported('The plan contains a stage that this model does not support.');

  let state = start;
  const outcomes: WaterStepOutcome[] = [];
  for (let index = 0; index < plan.length; index += 1) {
    const outcome = runWaterStep(plan[index] as WaterStepId, state);
    outcomes.push(outcome);
    if (outcome.status === 'failed') {
      return {
        status: 'failed',
        outcomes,
        failedAt: index,
        finalState: state,
        summary: `Stage ${index + 1} (${waterStepLabel(outcome.stepId)}) cannot work at this point. ${outcome.message}`,
      };
    }
    state = outcome.after;
  }
  if (state.contaminants.length > 0) {
    return {
      status: 'failed',
      outcomes,
      failedAt: -1,
      finalState: state,
      summary: `Not safe to drink yet: ${contaminantList(state.contaminants)} remain. Add the stage that removes them.`,
    };
  }
  const last = plan[plan.length - 1] as WaterStepId;
  if (scenario.contaminants.includes('microbes') && !DISINFECTION_STEPS.includes(last)) {
    return {
      status: 'failed',
      outcomes,
      failedAt: -1,
      finalState: state,
      summary: 'Make disinfection the final barrier. Water treated after the disinfection stage could pick up microbes again before it leaves the plant.',
    };
  }
  return {
    status: 'success',
    outcomes,
    failedAt: -1,
    finalState: state,
    summary: 'Every stage worked at its point in the train, and each contaminant group was removed by the stage that targets it.',
  };
}

export function validWaterPlans(scenarioId: string, maxSteps = MAX_PLAN_STEPS + 1): WaterStepId[][] {
  const scenario = getWaterScenario(scenarioId);
  if (!scenario) return [];
  const found: WaterStepId[][] = [];
  const search = (plan: WaterStepId[], state: WaterState) => {
    if (plan.length > 0 && runWaterPlan(scenarioId, plan).status === 'success') {
      found.push(plan);
      return;
    }
    if (plan.length >= maxSteps) return;
    for (const step of WATER_STEPS) {
      if (plan.includes(step.id)) continue;
      const outcome = runWaterStep(step.id, state);
      if (outcome.status === 'worked') search([...plan, step.id], outcome.after);
    }
  };
  search([], { contaminants: [...scenario.contaminants], chlorineResidual: false });
  return found.sort((a, b) => a.length - b.length);
}
